import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5100/api';
const BACKEND_URL = API_URL.replace('/api', '');

// Helper to get full image URL (for local storage uploads)
export const getImageUrl = (url) => {
  if (!url) return null;
  // If already absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // If relative path starting with /uploads, prepend backend URL
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const pathname = window.location.pathname;

    // Public pages that should not redirect on errors
    const isPublicPage = pathname === '/' ||
                         pathname === '/login' ||
                         pathname === '/creator-login' ||
                         pathname === '/register' ||
                         pathname.startsWith('/register/') ||
                         pathname === '/campaigns' ||
                         pathname.startsWith('/campaigns/') ||
                         pathname === '/about' ||
                         pathname === '/contact' ||
                         pathname.startsWith('/lp/');

    // Don't redirect on public pages - let the error be handled by the component
    if (isPublicPage) {
      return Promise.reject(error);
    }

    // Only force-logout on a real auth rejection (401). Network errors and 5xx
    // are transient — clearing the token on those was causing the user to be
    // logged out on refresh, when in-flight requests are aborted by the browser.
    if (error.response?.status === 401) {
      clearAuthStorage();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const campaignsApi = {
  getAll: (params) => api.get('/campaigns', { params }),
  getById: (id) => api.get(`/campaigns/${id}`),
  getMy: (params) => api.get('/campaigns/my', { params }),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  updateStatus: (id, status) => api.put(`/campaigns/${id}/status`, { status }),
  delete: (id) => api.delete(`/campaigns/${id}`)
};

export const applicationsApi = {
  apply: (campaignId, data) => api.post(`/applications/${campaignId}`, data),
  getMy: (params) => api.get('/applications/my', { params }),
  getById: (id) => api.get(`/applications/${id}`),
  getStats: () => api.get('/applications/stats'),
  withdraw: (id) => api.delete(`/applications/${id}`),
  getCampaign: (campaignId, params) => api.get(`/campaigns/${campaignId}/applications`, { params }),
  updateStatus: (id, data) => api.put(`/applications/${id}/status`, data)
};

export const messagesApi = {
  getAll: (params) => api.get('/messages', { params }),
  getById: (id) => api.get(`/messages/${id}`),
  markRead: (id) => api.put(`/messages/${id}/read`),
  markAllRead: () => api.put('/messages/mark-all-read'),
  getUnreadCount: () => api.get('/messages/unread-count'),
  delete: (id) => api.delete(`/messages/${id}`)
};

export const usersApi = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  getCreators: (params) => api.get('/users/creators', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getStats: () => api.get('/users/stats/me'),
  signAgreement: (data) => api.post('/users/sign-agreement', data)
};

export const uploadApi = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfile: (file) => {
    const formData = new FormData();
    formData.append('profile', file);
    return api.post('/uploads/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (filename) => api.delete(`/uploads/${filename}`)
};

// Leads API (public endpoint for brand registration)
export const leadsApi = {
  create: (data) => api.post('/leads', data)
};

// Admin API
export const adminApi = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),
  getStats: () => api.get('/admin/stats'),
  getActivity: () => api.get('/admin/activity'),

  // Creators (Influencers)
  getCreators: (params) => api.get('/admin/creators', { params }),
  approveCreator: (id) => api.put(`/admin/creators/${id}/approve`),
  rejectCreator: (id) => api.put(`/admin/creators/${id}/reject`),
  pendingCreator: (id) => api.put(`/admin/creators/${id}/pending`),
  deleteCreator: (id) => api.delete(`/admin/creators/${id}`),

  // Brands (registered brand users)
  getBrands: (params) => api.get('/admin/brands', { params }),
  deleteBrand: (id) => api.delete(`/admin/brands/${id}`),

  // Applications
  getApplications: (params) => api.get('/admin/applications', { params }),
  updateApplicationStatus: (id, status, adminNotes) =>
    api.put(`/admin/applications/${id}/status`, { status, adminNotes }),
  deleteApplication: (id) => api.delete(`/admin/applications/${id}`),

  // Campaigns
  getCampaigns: (params) => api.get('/admin/campaigns', { params }),
  createCampaign: (data) => api.post('/admin/campaigns', data),
  updateCampaign: (id, data) => api.put(`/admin/campaigns/${id}`, data),

  // Messages
  sendMessage: (data) => api.post('/admin/messages', data)
};
